import { Component, Input, OnInit, AfterViewInit, OnChanges, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators, FormArray } from '@angular/forms';
import { ServiceRouter } from '../../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../../constants/service.constants';
import { Environment } from '../../../../config/environment';
import { Config } from '../../../../config/config';
import { MatStepper } from '@angular/material/stepper';
import { ValidationUtilityService } from '../../../shared/utils/validation.util/validation-utility.service';
import { MASK_CONSTANTS } from 'src/app/constants/mask.constants';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';

/**
 * Add details component
 */
@Component({
	selector: 'app-ehe-add-details',
	templateUrl: './add-details.component.html',
	styleUrls: ['./add-details.component.scss'],
})
export class AddDetailsComponent implements OnInit, OnChanges {
	/** Add details form group */
	addDetailsForm: FormGroup<{
		details: FormArray<
			FormGroup<{
				reason: FormControl<string>;
				providerChoice: FormControl<string>;
				specialty: FormGroup<unknown>;
				provider: FormGroup<{
					firstName: FormControl<string>;
					lastName: FormControl<string>;
					phone: FormControl<string>;
					fax: FormControl<string>;
					address: FormControl<string>;
					address2: FormControl<string>;
					city: FormControl<string>;
					state: FormControl<string>;
					zip: FormControl<string>;
				}>;
			}>
		>;
	}>;
	/** Details form array */
	details: FormArray;
	/** Provider details */
	providerDetails: any = { referral: [] };
	/** Provider details array */
	providerDetailsArr = [];
	/** Specialty input */
	@Input() speciality: any;
	/** Details output emitter */
	@Output() getDetails: EventEmitter<any>;
	/** States array */
	states = [];
	/** Stepper input */
	@Input() stepper: MatStepper;
	/** Form reset output emitter */
	@Output() resetEvent = new EventEmitter<any>();
	/** Zip display mask */
	zipMask: any = MASK_CONSTANTS.ZIP;
	/** Phone number display mask */
	phoneMask: any = MASK_CONSTANTS.PHONE;
	/** Analytics bean */
	analyticsData: AnalyticsBean;

	/**
	 * Add details constructor
	 * @param serviceRouter service router
	 * @param environment environment file that supplies env values
	 * @param config config file that holds the service end points
	 * @param validationService Field validation service
	 */
	constructor(
		private serviceRouter: ServiceRouter,
		private environment: Environment,
		private config: Config,
		private validationService: ValidationUtilityService
	) {
		this.getDetails = new EventEmitter<any>();
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'add-details';
		this.analyticsData.pageUrl = '/wellness/referral/request';
	}

	/**
	 * Component initialization
	 * Calls methods to initialize add details form and retrieve states list
	 */
	ngOnInit() {
		this.createAddDetailsForm([]);
		this.getStates();
	}

	/**
	 * Component change detection
	 * to detect changes in the specialities selected previously
	 * and update form correspondingly
	 */
	ngOnChanges(changes) {
		if (
			changes.speciality.currentValue !== undefined &&
			changes.speciality.currentValue.specialities !== undefined &&
			changes.speciality.currentValue.specialities.length > 0
		) {
			this.createAddDetailsForm(changes.speciality.currentValue.specialities);
		}
	}

	/**
	 * Constructing form with speciality
	 * @param speciality - list of specialities selected by user from select speciality tab
	 */
	createAddDetailsForm(speciality) {
		this.providerDetailsArr = [];
		this.addDetailsForm = new FormGroup({
			details: new FormArray(speciality.map(tempSpeciality => this.detailsForm())),
		});
	}

	/**
	 * Constructing add details form
	 */
	detailsForm() {
		const providerDetailsForm = new FormGroup({
			reason: new FormControl('', Validators.compose([Validators.required])),
			providerChoice: new FormControl('', Validators.compose([Validators.required])),
			specialty: new FormGroup({}),
			provider: new FormGroup({
				firstName: new FormControl(
					'',
					Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(50)])
				),
				lastName: new FormControl(
					'',
					Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(50)])
				),
				phone: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.phoneNumberValidator])
				),
				fax: new FormControl(''),
				address: new FormControl('', Validators.required),
				address2: new FormControl(''),
				city: new FormControl('', Validators.required),
				state: new FormControl('', Validators.required),
				zip: new FormControl('', Validators.compose([Validators.required, ValidationUtilityService.zipCodeValidator])),
			}),
		});
		this.providerDetailsArr.push(providerDetailsForm);
		providerDetailsForm.get('providerChoice').valueChanges.subscribe(value => {
			if (value === '2') {
				providerDetailsForm.get('provider').enable();
			} else {
				providerDetailsForm.get('provider').disable();
			}
		});
		return providerDetailsForm;
	}

	/**
	 * Retrieves states list
	 */
	getStates() {
		this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.schedulingApi.getStates,
				METHOD_TYPE.SECURED_GET,
				''
			)
			.subscribe(
				response => {
					this.states = response;
				},
				error => {}
			);
	}

	/**
	 * On clicking next, the provider details are sent to specify preferences tab
	 */
	getProviderDetails() {
		this.analyticsData.id = 'Go Next to specify preferences';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral add details';
		if (this.addDetailsForm.valid) {
			this.stepper.next();
			const addDetailsBean = this.addDetailsForm.getRawValue().details;
			let count = 0;
			for (const i of addDetailsBean) {
				if (i.providerChoice === '1') {
					delete i.provider;
				}
				if (this.speciality.specialitiesBean && this.speciality.specialitiesBean.length > 0) {
					i.specialty = this.speciality.specialitiesBean[count];
				} else {
					i.specialty = this.speciality.specialities[count];
				}
				count += 1;
			}
			this.providerDetails.referral = addDetailsBean;

			this.providerDetails.referral.forEach(referral => {
				const provider = referral.provider;
				referral.provider = [];
				if (provider) {
					referral.provider.push(provider);
				} else {
					delete referral.provider;
				}
			});

			this.getDetails.emit(this.providerDetails);
		} else {
			this.throwValidationErrors();
		}
	}

	/**
	 * Cancel and reset form details
	 */
	cancel() {
		this.analyticsData.id = 'Cancel add details';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral add details';
		this.stepper.reset();
		this.resetEvent.emit(true);
		this.addDetailsForm.reset();
	}

	/**
	 * Throw validation errors on form fields
	 */
	throwValidationErrors() {
		const d = this.addDetailsForm.get('details')['controls'];
		for (const form of d) {
			form.get('reason').markAsTouched();
			form.get('providerChoice').markAsTouched();
			if (form.get('providerChoice').value === '2') {
				form.get('provider').get('firstName').markAsTouched();
				form.get('provider').get('lastName').markAsTouched();
				form.get('provider').get('phone').markAsTouched();
				form.get('provider').get('address').markAsTouched();
				form.get('provider').get('city').markAsTouched();
				form.get('provider').get('state').markAsTouched();
				form.get('provider').get('zip').markAsTouched();
			}
		}
	}

	getError(index, controlName) {
		if (
			this.addDetailsForm.get('details')[index] !== undefined &&
			this.addDetailsForm.get('details')[index].get(controlName).touched
		) {
			const error = this.addDetailsForm.get('details')[index].get(controlName).errors;
			if (error) {
				return this.validationService.get(error);
			} else {
				return '';
			}
		}
	}

	/**
	 * Navigates back to select specialty form
	 */
	goBack() {
		this.analyticsData.id = 'Back to select specialty';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral add details';
		this.addDetailsForm.reset();
		this.stepper.previous();
	}
}
